import { ApiError } from "@groundspark/browser-auth";

export interface CanvassingProject {
  readonly id: string;
  readonly name: string;
  readonly electoralRegisterId: string | null;
  readonly updatedAt: string;
  readonly canvassedCount: number;
}

export const CANVASS_OUTCOMES = [
  ["yes", "Yes", "--canvass-yes"],
  ["considering", "Considering", "--canvass-considering"],
  ["undecided", "Undecided", "--canvass-undecided"],
  ["other_party", "Other Party", "--canvass-other-party"],
  ["not_interested", "Not Interested", "--canvass-not-interested"],
  ["no", "No", "--canvass-no"],
  ["non_voter", "Non-Voter", "--canvass-non-voter"],
  ["not_recorded", "Not recorded", "--canvass-notes-only"],
] as const;

export const CANVASS_VOTER_STATUSES = [
  ["moved", "Moved", "--canvass-moved"],
  ["out", "Out", "--canvass-out"],
  ["blacklist", "Blacklist", "--canvass-blacklist"],
  ["not_recorded", "Not recorded", "--canvass-notes-only"],
] as const;

export const CANVASS_RESULT_FIELDS = [
  ["general_outcome", "General elections"],
  ["local_outcome", "Local elections"],
  ["voter_status", "Voter status"],
] as const;

export type CanvassResultField = (typeof CANVASS_RESULT_FIELDS)[number][0];

export function canvassLegend(field: CanvassResultField) {
  return field === "voter_status" ? CANVASS_VOTER_STATUSES : CANVASS_OUTCOMES;
}

export function parseCanvassingProjects(value: unknown): readonly CanvassingProject[] {
  if (!Array.isArray(value)) throw new ApiError("Invalid canvassing project list.");
  const seen = new Set<string>();
  return value.map((item: unknown) => {
    if (item === null || typeof item !== "object") {
      throw new ApiError("Invalid canvassing project.");
    }
    const row = item as Record<string, unknown>;
    if (
      typeof row.id !== "string" ||
      !/^[a-zA-Z0-9_-]{1,64}$/.test(row.id) ||
      seen.has(row.id) ||
      typeof row.name !== "string" ||
      row.name.trim().length === 0 ||
      !(
        row.electoral_register_id === null ||
        (typeof row.electoral_register_id === "string" && row.electoral_register_id.length > 0)
      ) ||
      typeof row.updated_at !== "string" ||
      !Number.isFinite(Date.parse(row.updated_at)) ||
      typeof row.canvassed_count !== "number" ||
      !Number.isSafeInteger(row.canvassed_count) ||
      row.canvassed_count < 0
    )
      throw new ApiError("Invalid canvassing project metadata.");
    seen.add(row.id);
    return {
      id: row.id,
      name: row.name,
      electoralRegisterId: row.electoral_register_id,
      updatedAt: row.updated_at,
      canvassedCount: row.canvassed_count,
    };
  });
}
